import {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import fieldStyles from "./FormField.module.css";
import styles from "./Select.module.css";

const CLOSE_ANIMATION_MS = 160;

interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

interface Group {
  label: string;
  options: Option[];
}

type Item = { kind: "option"; option: Option } | { kind: "group"; group: Group };

/** Lê os filhos <option>/<optgroup> passados como no <select> nativo de antes — assim as
 * telas que usam <Select> continuam escrevendo as opções exatamente como já escreviam. */
function parseChildren(children: ReactNode): Item[] {
  const items: Item[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const props = child.props as Record<string, unknown>;
    if (child.type === "option") {
      items.push({
        kind: "option",
        option: {
          value: String(props.value ?? ""),
          label: String(props.children ?? ""),
          disabled: Boolean(props.disabled),
        },
      });
    } else if (child.type === "optgroup") {
      const options: Option[] = [];
      Children.forEach(props.children as ReactNode, (grandchild) => {
        if (isValidElement(grandchild) && grandchild.type === "option") {
          const gp = grandchild.props as Record<string, unknown>;
          options.push({
            value: String(gp.value ?? ""),
            label: String(gp.children ?? ""),
            disabled: Boolean(gp.disabled),
          });
        }
      });
      items.push({ kind: "group", group: { label: String(props.label ?? ""), options } });
    }
  });
  return items;
}

function flattenOptions(items: Item[]): Option[] {
  return items.flatMap((item) => (item.kind === "option" ? [item.option] : item.group.options));
}

interface SelectProps {
  label?: string;
  error?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Combobox customizado que substitui o <select> nativo. O menu do <select> nativo é
 * desenhado pelo sistema operacional — CSS não consegue estilizar nem animar as opções
 * dele. Aqui o painel é HTML/CSS nosso, então dá pra animar a abertura/fechamento de
 * verdade. Mesma API de fora: `<Select value onChange>` com filhos <option>/<optgroup>.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  ({ label, error, value, defaultValue, onChange, onBlur, name, id, disabled, className, children }, ref) => {
    const selectId = id ?? name;
    const items = useMemo(() => parseChildren(children), [children]);
    const flatOptions = useMemo(() => flattenOptions(items), [items]);

    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState<string>(
      () => (isControlled ? value! : (defaultValue ?? ""))
    );
    useEffect(() => {
      if (isControlled) setInternalValue(value!);
    }, [value, isControlled]);

    const [open, setOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const closeTimer = useRef<number>();

    const close = () => {
      setClosing(true);
      window.clearTimeout(closeTimer.current);
      closeTimer.current = window.setTimeout(() => {
        setOpen(false);
        setClosing(false);
      }, CLOSE_ANIMATION_MS);
    };

    useEffect(() => () => window.clearTimeout(closeTimer.current), []);

    useEffect(() => {
      if (!open) return;
      const handleClick = (e: MouseEvent) => {
        if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
      };
      const handleKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          // Impede o Escape de "vazar" e fechar também o modal por trás (ex: o wizard de
          // novo lançamento) — aqui ele deve fechar só o menu do combobox.
          e.stopPropagation();
          close();
        }
      };
      document.addEventListener("mousedown", handleClick);
      document.addEventListener("keydown", handleKey);
      return () => {
        document.removeEventListener("mousedown", handleClick);
        document.removeEventListener("keydown", handleKey);
      };
    }, [open]);

    const pick = (next: string) => {
      if (!isControlled) setInternalValue(next);
      onChange?.(next);
      close();
    };

    const selected = flatOptions.find((o) => o.value === internalValue && !o.disabled);
    const placeholderOption = flatOptions.find((o) => o.disabled && o.value === "");

    const renderOption = (option: Option) =>
      option.disabled ? null : (
        <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={option.value === internalValue}
          className={cn(styles.option, option.value === internalValue && styles.optionActive)}
          onClick={() => pick(option.value)}
        >
          <span>{option.label}</span>
        </button>
      );

    return (
      <div className={fieldStyles.wrapper} ref={wrapRef}>
        {label && (
          <label htmlFor={selectId} className={fieldStyles.label}>
            {label}
          </label>
        )}
        <div className={styles.combo}>
          <button
            ref={ref}
            type="button"
            id={selectId}
            disabled={disabled}
            className={cn(fieldStyles.control, styles.trigger, error && fieldStyles.controlError, className)}
            onClick={() => (open ? close() : setOpen(true))}
            onBlur={onBlur}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className={cn(styles.triggerLabel, !selected && styles.placeholder)}>
              {selected ? selected.label : (placeholderOption?.label ?? "")}
            </span>
            <ChevronDown size={16} className={cn(styles.chevron, open && styles.chevronOpen)} />
          </button>

          {open && (
            <div className={cn(styles.panel, closing && styles.panelClosing)} role="listbox">
              {items.map((item, i) =>
                item.kind === "group" ? (
                  <div key={i} className={styles.group}>
                    <p className={styles.groupLabel}>{item.group.label}</p>
                    {item.group.options.map(renderOption)}
                  </div>
                ) : (
                  renderOption(item.option)
                )
              )}
            </div>
          )}
        </div>
        {error && <span className={fieldStyles.errorText}>{error}</span>}
      </div>
    );
  }
);

Select.displayName = "Select";
